import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Shield, Plus, Trash2, Edit, Zap, Database, Phone, MessageSquare,
  FileCheck, ArrowLeft, Lock, RefreshCw, Mail, Cloud, CloudOff
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  "Received","Being Diagnosed","In Progress","Warranty Repair","Customer Reply",
  "Waiting on Customer","Waiting on Approval","Waiting on Parts",
  "Parts Received Waiting on Customer","Shipped off / Partner Company",
  "File List Sent","Diagnostics Complete","Pending Quote Approval",
  "Repaired","Data Recovery Complete","Closed","Canceled"
];
const DEVICE_TYPES = ["apple","laptop","desktop","storage","phone","other"];
const TECHS = ["Luke","Sarah","Mike","John"];

function TicketForm({ ticket, onSave, onClose }) {
  const [form, setForm] = useState(ticket || {
    ticket_number: `#${Math.floor(4000 + Math.random() * 1000)}`,
    device_name: "", device_type: "laptop", repair_type: "",
    assigned_technician: "Luke", status: "Received",
    is_rush: false, has_tracking: false, has_eta: false, notes: ""
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      if (ticket?.id) {
        await axios.put(`${API}/tickets/${ticket.id}`, form);
      } else {
        await axios.post(`${API}/tickets`, form);
      }
      onSave();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-400">Ticket #</Label>
          <Input data-testid="form-ticket-number" value={form.ticket_number} onChange={e => set("ticket_number", e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Device Name</Label>
          <Input data-testid="form-device-name" value={form.device_name} onChange={e => set("device_name", e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-400">Device Type</Label>
          <Select value={form.device_type} onValueChange={v => set("device_type", v)}>
            <SelectTrigger data-testid="form-device-type" className="bg-slate-900 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {DEVICE_TYPES.map(d => <SelectItem key={d} value={d} className="text-slate-200">{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Repair Type</Label>
          <Input data-testid="form-repair-type" value={form.repair_type} onChange={e => set("repair_type", e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-400">Technician</Label>
          <Select value={form.assigned_technician} onValueChange={v => set("assigned_technician", v)}>
            <SelectTrigger data-testid="form-technician" className="bg-slate-900 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {TECHS.map(t => <SelectItem key={t} value={t} className="text-slate-200">{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Status</Label>
          <Select value={form.status} onValueChange={v => set("status", v)}>
            <SelectTrigger data-testid="form-status" className="bg-slate-900 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {STATUSES.map(s => <SelectItem key={s} value={s} className="text-slate-200">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch data-testid="form-rush" checked={form.is_rush} onCheckedChange={v => set("is_rush", v)} />
          <Label className="text-xs text-slate-400">Rush Job</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch data-testid="form-tracking" checked={form.has_tracking} onCheckedChange={v => set("has_tracking", v)} />
          <Label className="text-xs text-slate-400">Has Tracking</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch data-testid="form-eta" checked={form.has_eta} onCheckedChange={v => set("has_eta", v)} />
          <Label className="text-xs text-slate-400">Has ETA</Label>
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-400">Notes</Label>
        <Input data-testid="form-notes" value={form.notes} onChange={e => set("notes", e.target.value)} className="bg-slate-900 border-slate-700 text-slate-200" />
      </div>
      <div className="flex gap-2 justify-end mt-2">
        <Button data-testid="form-cancel" variant="outline" size="sm" onClick={onClose} className="border-slate-700 text-slate-400">Cancel</Button>
        <Button data-testid="form-save" size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [tickets, setTickets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTicket, setEditTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rdStatus, setRdStatus] = useState(null);
  const navigate = useNavigate();

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/all`);
      setTickets(data.tickets || []);
    } catch {}
  }, []);

  const fetchRdStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/repairdesk/status`);
      setRdStatus(data);
    } catch {}
  }, []);

  useEffect(() => { if (authed) { fetchTickets(); fetchRdStatus(); } }, [authed, fetchTickets, fetchRdStatus]);

  const login = async () => {
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      setAuthed(true);
    } catch { alert("Invalid password"); }
  };

  const del = async (id) => {
    await axios.delete(`${API}/tickets/${id}`);
    fetchTickets();
  };

  const seed = async () => {
    setLoading(true);
    await axios.post(`${API}/tickets/seed`);
    await fetchTickets();
    setLoading(false);
  };

  const fireEvent = async (endpoint, ticketId) => {
    await axios.post(`${API}/events/${endpoint}`, { ticket_id: ticketId });
    fetchTickets();
  };

  const triggerRdSync = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/repairdesk/sync`);
      await fetchTickets();
      await fetchRdStatus();
    } catch {}
    setLoading(false);
  };

  const onSave = () => {
    setDialogOpen(false);
    setEditTicket(null);
    fetchTickets();
  };

  if (!authed) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#020617]">
        <div className="bg-[#0F172A] border border-white/10 rounded-lg p-8 w-80">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-blue-400" />
            <span className="font-display font-bold text-lg text-slate-100">Admin Access</span>
          </div>
          <Input
            data-testid="admin-password"
            type="password" placeholder="Enter password"
            value={pw} onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === "Enter" && login()}
            className="bg-slate-900 border-slate-700 text-slate-200 mb-3"
          />
          <Button data-testid="admin-login-btn" onClick={login} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Authenticate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 p-4">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button data-testid="admin-back" variant="ghost" size="sm" onClick={() => navigate("/")} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Board
          </Button>
          <Shield className="w-5 h-5 text-blue-400" />
          <span className="font-display font-bold text-lg tracking-wider">ADMIN PANEL</span>
          <Badge variant="outline" className="text-xs text-slate-400 border-slate-700">{tickets.length} tickets</Badge>
          {rdStatus?.configured && (
            <div className="flex items-center gap-1.5">
              {rdStatus.last_sync ? (
                <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-0 gap-1">
                  <Cloud className="w-3 h-3" /> RepairDesk Connected ({rdStatus.ticket_count} synced)
                </Badge>
              ) : (
                <Badge className="text-[10px] bg-red-500/15 text-red-400 border-0 gap-1">
                  <CloudOff className="w-3 h-3" /> Sync pending
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="admin-seed" variant="outline" size="sm" onClick={seed} disabled={loading} className="border-slate-700 text-slate-300 hover:text-white">
            <Database className="w-4 h-4 mr-1" /> Seed Demo
          </Button>
          {rdStatus?.configured && (
            <Button data-testid="admin-rd-sync" variant="outline" size="sm" onClick={triggerRdSync} disabled={loading} className="border-emerald-700 text-emerald-400 hover:text-emerald-300">
              <Cloud className="w-4 h-4 mr-1" /> {loading ? "Syncing..." : "Sync RepairDesk"}
            </Button>
          )}
          <Button data-testid="admin-refresh" variant="outline" size="sm" onClick={fetchTickets} className="border-slate-700 text-slate-300 hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if(!v) setEditTicket(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="admin-create" size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-1" /> New Ticket
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-slate-700 text-slate-200 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">{editTicket ? "Edit Ticket" : "New Ticket"}</DialogTitle>
              </DialogHeader>
              <TicketForm ticket={editTicket} onSave={onSave} onClose={() => { setDialogOpen(false); setEditTicket(null); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Ticket table */}
      <div className="bg-[#0F172A] border border-white/[0.08] rounded-lg overflow-auto max-h-[calc(100vh-160px)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 sticky top-0">
            <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Repair</th>
              <th className="px-3 py-2">Tech</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2">Events</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr key={t.id} className="border-t border-white/[0.04] hover:bg-slate-800/30">
                <td className="px-3 py-2 font-display text-slate-400">{t.ticket_number}</td>
                <td className="px-3 py-2">
                  <span className="text-slate-200">{t.device_name}</span>
                  <span className="text-xs text-slate-600 ml-1">({t.device_type})</span>
                </td>
                <td className="px-3 py-2 text-slate-400">{t.repair_type}</td>
                <td className="px-3 py-2 text-slate-400">{t.assigned_technician}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-300">{t.status}</Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {t.is_rush && <Badge className="text-[10px] bg-red-500/20 text-red-400 border-0">Rush</Badge>}
                    {t.has_tracking && <Badge className="text-[10px] bg-blue-500/20 text-blue-400 border-0">Tracking</Badge>}
                    {t.source === 'repairdesk' && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-0">RD</Badge>}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <Button data-testid={`evt-reply-${t.ticket_number}`} variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-slate-500 hover:text-blue-400" onClick={() => fireEvent("customer-reply", t.id)} title="Customer Reply">
                      <MessageSquare className="w-3 h-3" />
                    </Button>
                    <Button data-testid={`evt-approval-${t.ticket_number}`} variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-slate-500 hover:text-green-400" onClick={() => fireEvent("approval", t.id)} title="Approval">
                      <FileCheck className="w-3 h-3" />
                    </Button>
                    <Button data-testid={`evt-call-${t.ticket_number}`} variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-slate-500 hover:text-amber-400" onClick={() => fireEvent("phone-call", t.id)} title="Phone Call">
                      <Phone className="w-3 h-3" />
                    </Button>
                    <Button data-testid={`evt-note-${t.ticket_number}`} variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-slate-500 hover:text-emerald-400" onClick={() => fireEvent("note-added", t.id)} title="Add Note">
                      <Zap className="w-3 h-3" />
                    </Button>
                    <Button data-testid={`evt-email-${t.ticket_number}`} variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-slate-500 hover:text-cyan-400" onClick={() => fireEvent("email-detected", t.id)} title="Email Detected">
                      <Mail className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button data-testid={`edit-${t.ticket_number}`} variant="ghost" size="sm" className="h-6 px-1.5 text-slate-500 hover:text-white"
                      onClick={() => { setEditTicket(t); setDialogOpen(true); }}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button data-testid={`delete-${t.ticket_number}`} variant="ghost" size="sm" className="h-6 px-1.5 text-slate-500 hover:text-red-400"
                      onClick={() => del(t.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tickets.length === 0 && (
          <div className="text-center py-12 text-slate-600">
            No tickets. Click "Seed Demo Data" to populate test data.
          </div>
        )}
      </div>
    </div>
  );
}
